(ns manage.ad-variant.creation.ad-builder.table
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/AlertTitle$default" :as AlertTitle]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Switch$default" :as Switch]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium]]
            ["src/svg/objects/rectangle-split-top-and-left.svg$default" :as RectangleSplitTopAndLeft]
            ["src/svg/wpp/content-actions/edit.svg$default" :as Edit]
            [clojure.string :as str]
            [common.modal.util :refer [with-confirmation-modal]]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.adset :as adset.q]
            [common.widgets.column-renderers :refer [template-duration-renderer]]
            [common.widgets.data-grid :as dg]
            [manage.ad-variant.creation.ad-builder.hooks :as h]
            [manage.ad-variant.creation.ad-builder.queries :as q]
            [manage.ad-variant.data :as data]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defn- ad-format-has-new-revision? [ad-format]
  (let [current-revision-id (get-in ad-format [:current-revision :id])
        latest-revision-id (get-in ad-format [:latest-revision :id])]
    (and (not= latest-revision-id current-revision-id)
         (not (nil? latest-revision-id)))))

(defui active-cell [{active :value ad-builder :row}]
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {patch-ad-builders :mutate} (use-mutation (q/patch-ad-builders {:adset-id adset-id}))]
    ($ Tooltip
       {:title "Control whether this ad builder can create ad variants"
        :placement "top"}
       ($ Switch
          {:checked active
           :onChange (fn []
                       (with-confirmation-modal active
                         :ad-builder/deactivation-warning
                         {}
                         #(patch-ad-builders (h/->update-ad-builder-payload (update ad-builder :active not)))))
           :onClick #(.stopPropagation %)}))))

(defui actions-cell [{ad-builder :row}]
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {put-ad-formats :mutate} (use-mutation (q/put-ad-formats {:adset-id adset-id}))]
    ($ :<>
       (when (ad-format-has-new-revision? (get-in ad-builder [:ad-formats 0]))
         ($ Stack
            {:direction "row"
             :alignItems "center"
             :gap 1}
            ($ Chip
               {:label "New revision available"
                :variant "subtle"
                :color "primary"
                :sx #js {:fontWeight "600"
                         :textTransform "uppercase"}})
            ($ Tooltip {:title "Update template revision"}
               ($ IconButton
                  {:onClick #(put-ad-formats (h/->update-template-revisions-payload [ad-builder]))}
                  ($ Icon "autorenew")))))
       ($ Tooltip {:title "Change template"}
          ($ IconButton
             {:onClick #(rf/dispatch [:modal/open {:id :ad-builder/change-template-modal
                                                   :props {:ad-builder ad-builder}}])}
             ($ Edit))))))

(defn- columns [after-effects-templates?]
  (->> [#js {:field "active"
             :headerName "Active"
             :type "boolean"
             :resizable false
             :valueGetter (fn [_value ^js row ^js _column ^js _apiRef]
                            (:active row))
             :renderCell (dg/get-cell-renderer active-cell)}
        #js {:field "urn"
             :headerName "Format"
             :type "string"
             :minWidth 140
             :flex 2
             :valueGetter (fn [_value ^js row ^js _column ^js _apiRef]
                            (:urn row))
             :renderCell (fn [^js params]
                           (some-> (.-value params) data/ad-builder-urn->title))}
        #js {:field "template"
             :headerName "Template"
             :type "string"
             :minWidth 200
             :flex 2
             :valueGetter (fn [_value ^js row ^js _column ^js _apiRef]
                            (:name row))}
        #js {:field "dimensions"
             :headerName "Dimensions"
             :type "string"
             :minWidth 140
             :flex 1
             :valueGetter (fn [_value ^js row ^js _column ^js _apiRef]
                            (let [{:keys [width height]} (get-in row [:ad-formats 0 :current-revision])]
                              (if-not (or (str/blank? width) (str/blank? height))
                                (str width "x" height)
                                "")))}
        (when after-effects-templates?
          #js {:field "duration"
               :headerName "Duration"
               :type "string"
               :width 90
               :valueGetter (fn [_value ^js row ^js _column ^js _apiRef]
                              (get-in row [:ad-formats 0 :current-revision :duration]))
               :renderCell (fn [^js params]
                             (template-duration-renderer (.-value params)))})
        #js {:field "revision"
             :headerName "Revision"
             :type "number"
             :width 90
             :valueGetter (fn [_value ^js row ^js _column ^js _apiRef]
                            (get-in row [:ad-formats 0 :current-revision :version]))}
        #js {:field "actions"
             :headerName ""
             :type "actions"
             :align "right"
             :minWidth 260
             :renderCell (dg/get-cell-renderer actions-cell)}]
       (filter some?)
       to-array))

(def Toolbar
  (uix/as-react
   (fn [{:keys [ad-builders]}]
     (let [{:keys [adset-id]} (use-subscribe [:url-context])
           {put-ad-formats :mutate} (use-mutation (q/put-ad-formats {:adset-id adset-id}))

           ad-format-rows (uix/use-memo
                           (fn []
                             (mapcat :ad-formats ad-builders))
                           [ad-builders])]
       ($ Stack
          ($ dg/grid-toolbar
             ($ Button
                {:variant "contained"
                 :color "grey"
                 :component "a"
                 :startIcon ($ Icon "add")
                 :onClick #(rf/dispatch [:modal/open {:id :ad-builder/add-template-modal}])}
                "Add ad builder"))
          (when (some ad-format-has-new-revision? ad-format-rows)
            ($ Alert
               {:icon ($ SvgIcon
                         {:component RectangleSplitTopAndLeft})
                :severity "info"}
               ($ AlertTitle {} "New template revisions available!")
               ($ Button
                  {:variant "contained"
                   :startIcon ($ Icon "autorenew")
                   :onClick #(put-ad-formats (h/->update-template-revisions-payload ad-builders))
                   :sx #js {:mt 0.5}}
                  "Update all revisions"))))))))

(defn- get-row-id [row]
  (:id row))

(defui ad-builders-table [{:keys [ad-builders-upgrade-hints-reponse]}]
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {{:keys [channel]} :data} (use-query (adset.q/adset-query {:adset-id adset-id}))
        {:keys [isFetching data]} ad-builders-upgrade-hints-reponse

        page-size 10

        columns (columns (not= channel "display"))]
    ($ Stack
       {:flex 1
        :sx #js {:overflow "hidden"}}
       ($ DataGridPremium
          {:columns columns

           :loading isFetching

           :rows (to-array data)
           :getRowId get-row-id

           :disableRowSelectionOnClick true

           :pagination true
           :pageSizeOptions #js [page-size]
           :data-testid "table-ad-builders"

           :slots #js {:toolbar Toolbar}
           :slotProps #js {:baseTextField dg/grid-toolbar-outlined-input
                           :toolbar #js {:ad-builders data}}

           :noWrap true}))))
